import { useIBANValidation } from '@api/hooks/useIBANValidation.ts';
import { useDebouncedValidate } from '@hooks/useDebouncedValidate';
import { useVop } from '@hooks/useVop';
import { useEffect, useRef, useState } from 'react';
import type { ValidateResult } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VOP_STATUS } from 'types/layout';

const DEBOUNCE_MS = 500;

export const useSepaValidation = ({ watch }) => {
	const { t } = useTranslation();
	const { mutateAsync: validateIban, isPending: isIbanValidating } = useIBANValidation();
	const [vopParams, setVopParams] = useState<
		{ iban: string; name: string } | undefined
	>();
	const beneficiaryName = watch(`beneficiary.name`);
	const latestIbanRef = useRef<string | undefined>(undefined);

	const { data: vop, isLoading: isVopLoading, refetch: vopRefetch } = useVop(vopParams);

	const vopCheckFailed =
		!!vop && [VOP_STATUS.NO_MATCH, VOP_STATUS.CANNOT_VERIFY].includes(vop.match_status);

	// Functional update: only changes state (and re-enables useVop) if params actually differ.
	// Prevents cache invalidation when same IBAN+name are re-entered after editing.
	const updateVopParams = (
		iban: string | undefined,
		name: string | undefined,
		requiresVop: boolean,
	) => {
		setVopParams((prev) => {
			if (!requiresVop || !iban || !name) {
				return prev === undefined ? prev : undefined;
			}
			if (prev?.iban === iban && prev?.name === name) return prev;
			return { iban, name };
		});
	};

	// Clears vopParams immediately when name is cleared, so useVop disables and vop resets.
	useEffect(() => {
		if (!beneficiaryName) updateVopParams(undefined, undefined, false);
	}, [beneficiaryName]);

	// Debounced IBAN validate — also drives VOP params on IBAN change.
	// No onLeadingEdge clear: avoids disabling useVop and invalidating gcTime:0 cache.
	const validateIbanInput = useDebouncedValidate(
		async (ibanValue: string): Promise<ValidateResult> => {
			latestIbanRef.current = ibanValue;
			if (!ibanValue) {
				updateVopParams(undefined, undefined, false);
				return true;
			}
			try {
				const result = await validateIban(ibanValue);
				updateVopParams(ibanValue, beneficiaryName, result.requires_vop_check);
				return result.iban_checksum_valid || t('payments.newPayment.form.accountNoError');
				// eslint-disable-next-line unused-imports/no-unused-vars
			} catch (err) {
				return t('payments.newPayment.form.failedToValidate');
			}
		},
		DEBOUNCE_MS,
	);

	// Debounced name validate — drives VOP re-check when name changes.
	// Replaces the watch() subscription. Always returns true (name errors shown as VOP badge).
	const validateBeneficiaryName = useDebouncedValidate(
		async (nameValue: string): Promise<ValidateResult> => {
			if (!nameValue || !latestIbanRef.current) return true;
			const result = await validateIban(latestIbanRef.current); // cache hit
			updateVopParams(latestIbanRef.current, nameValue, result.requires_vop_check);
			return true;
		},
		DEBOUNCE_MS,
	);

	return {
		validateIbanInput,
		validateBeneficiaryName,
		isIbanValidating,
		vop,
		isVopLoading,
		vopCheckFailed,
		vopRefetch,
	};
};
